import { defineStore } from "pinia";
import { ref } from "vue";
import { Link, Channel, List } from "../models/link";
import { updateLinkUrl, convertResToLinks } from "./utils";
import api from "../plugins/api";

export const useChannelStore = defineStore('channels', () => {
    const allChannels = ref<Channel[]>([]);
    const channelLinks = ref<Link[]>([]);
    const channelQuickLinks = ref<Link[]>([]);
    const channelLists = ref<List[]>([]);
    const channel = ref({} as Channel);
    const isLoadingChannels = ref(false);

    async function fetchChannel(channelId: string) {
      if (channelId === channel.value.id) {
        return;
      } else {
        channel.value = {} as Channel;
      }

      try {
        const { data } = await api.get(`/channels/${channelId}`);
        channel.value = {
            id: data.id,
            name: data.name,
            imageUrl: data.imageUrl,
            youtubeCustomUrl: data.ytCustomUrl,
            subscriberCount: data.subscriberCount,
            nextRefreshAt: new Date(data.nextRefreshAt || '').toLocaleString(),
            lastUpdatedAt: new Date(data.linksUpdatedAt || '').toLocaleString(),
            accountLevel: data.accountLevel,
        } as Channel;
      } catch (error) {
          // handled by api interceptor
      }
    }

    async function getChannelClickCount(channelId: string) {
      try {
        const { data } = await api.get(`/channels/${channelId}/clicks`);
        return data;
      } catch (error) {
          // handled by api interceptor
      }
    }

    async function updateChannels() {
      try {
        await api.get(`/youtube/subscriptions`);
      } catch (error) {
          // handled by api interceptor
      }
    }

    async function fetchChannelLists(channelId: string) {
      try {
        const { data } = await api.get(`/channels/${channelId}/lists`);
        channelLists.value = data.map((el: any) => {
          return {
            id: el.id,
            name: el.name,
            imageUrl: el.links && el.links.length > 0 ? el.links[0].imageUrl : '',
            links: el.links.map(updateLinkUrl),
          } as List;
        });
      } catch (error) {
          // handled by api interceptor
      }
    }

    async function fetchChannelQuicklinks(channelId: string) {
      if (channelQuickLinks.value[0] && channelQuickLinks.value[0].channel.id === channelId) {
        return;
      } else {
        channelQuickLinks.value = [];
      }

      try {
        const { data } = await api.get(`/channels/${channelId}/quick-links`);
        channelQuickLinks.value = data.map(convertResToLinks);
      } catch (error) {
          // handled by api interceptor
      }
    }

    async function addQuicklink(channelId: string, linkId: string) {
      try {
        await api.post(`/channels/${channelId}/quick-links/${linkId}`);
        channelQuickLinks.value = [];
        await fetchChannelQuicklinks(channelId); 
      } catch (error) {
          // handled by api interceptor
      }
    }

    async function deleteQuicklink(channelId: string, linkId: string) {
      try {
        await api.delete(`/channels/${channelId}/quick-links/${linkId}`);
        channelQuickLinks.value = [];
        await fetchChannelQuicklinks(channelId); 
      } catch (error) {
          // handled by api interceptor
      }
    }

    async function fetchChannels() {
      isLoadingChannels.value = true;
      try {
        const { data } = await api.get(`/channels`);
        allChannels.value = [];
        channelLinks.value = [];
        data.forEach((el: any) => {
          let chan: Channel = {
            id: el.id,
            name: el.name,
            youtubeId: el.youtubeId,
            imageUrl: el.imageUrl,
            quickLinks: el.quickLinks
          };          
          allChannels.value.push(chan);
          channelLinks.value.push({id: chan.id, type: 'channel', channel: chan} as Link);
        });
      } catch (error) {
          // handled by api interceptor
      } finally {
        isLoadingChannels.value = false;
      }
    }

    function reset() {
      allChannels.value = [];
      channelLinks.value = [];
      channelLists.value = [];
      channel.value = {} as Channel;
      channelQuickLinks.value = [];
    }
  
    return {
      allChannels, getChannelClickCount, channelLinks, channelLists, channel, channelQuickLinks, isLoadingChannels,
      fetchChannels, fetchChannel, fetchChannelLists, fetchChannelQuicklinks, addQuicklink, deleteQuicklink, updateChannels, reset
    };
});